import { FormEvent, useCallback, useEffect, useState } from 'react';
import {
	Button,
	Flex,
	FormHelperText,
	Input,
	Select,
	Switch,
	Text,
	useDisclosure,
	useToast
} from '@chakra-ui/react';
import {
	InformativoGet,
	InformativoType,
	TipoInformativo,
	adicionarInformativo,
	deleteInformativo,
	updateInformativo
} from '../../services/api';
import { ArteInformativo } from '../../components/ArteInformativo';
import { CampoForm } from '../../components/CampoForm';
import { ConfirmarExclusao } from '../../components/ConfirmarExclusao';
import { DrawerForm } from '../../components/DrawerForm';
import { ErrosForm, useErrosForm } from '../../hooks/useErrosForm';
import { vazio } from '../../utils/validacao';
import { fimDoDiaISO, inicioDoDiaISO, paraDataLocal } from '../../utils/data';
import { toBase64, uploadFileWithBase64 } from '../../utils/imageHelper';

type DrawerInformativoProps = {
	isOpen: boolean;
	onClose: () => void;
	afterClose: () => void;
	/** Ausente = criação. Presente = edição do informativo selecionado. */
	informativo?: InformativoGet;
};

type Campo = 'titulo' | 'imagem' | 'janela';

export const TIPOS: { valor: TipoInformativo; rotulo: string }[] = [
	{ valor: 'INFORMATIVO', rotulo: 'Informativo' },
	{ valor: 'FERIADO', rotulo: 'Feriado' },
	{ valor: 'DATA_COMEMORATIVA', rotulo: 'Data comemorativa' },
	{ valor: 'NOVIDADE', rotulo: 'Novidade da escola' }
];

const TAMANHO_MAXIMO = 10000000;

/**
 * Um único drawer para criar e editar: os dois formulários seriam idênticos,
 * então duplicá-los só criaria duas cópias para manter em sincronia.
 */
export function DrawerInformativo({ isOpen, onClose, afterClose, informativo }: DrawerInformativoProps) {
	const toast = useToast();
	const confirmacao = useDisclosure();
	const { erros, validar, limparErro, limparTudo } = useErrosForm<Campo>();

	const [ loading, setLoading ] = useState(false);

	const [ titulo, setTitulo ] = useState('');
	const [ tipo, setTipo ] = useState<TipoInformativo>('INFORMATIVO');
	// Guarda a URL já publicada (edição) ou o base64 recém-escolhido (upload
	// pendente). O prefixo `data:` é o que diferencia os dois no submit.
	const [ imagem, setImagem ] = useState('');
	const [ inicioEm, setInicioEm ] = useState('');
	const [ fimEm, setFimEm ] = useState('');
	const [ ordem, setOrdem ] = useState('0');
	const [ ativo, setAtivo ] = useState(true);

	const editando = !!informativo;

	const resetForm = useCallback(
		() => {
			setTitulo(informativo?.titulo ?? '');
			setTipo(informativo?.tipo ?? 'INFORMATIVO');
			setImagem(informativo?.imagemUrl ?? '');
			setInicioEm(paraDataLocal(informativo?.inicioEm ?? null));
			setFimEm(paraDataLocal(informativo?.fimEm ?? null));
			setOrdem(`${informativo?.ordem ?? 0}`);
			setAtivo(informativo?.ativo ?? true);
			limparTudo();
		},
		[ informativo, limparTudo ]
	);

	useEffect(
		() => {
			if (isOpen) resetForm();
		},
		[ isOpen, resetForm ]
	);

	const montarErros = (): ErrosForm<Campo> => {
		const encontrados: ErrosForm<Campo> = {};

		if (vazio(titulo)) encontrados.titulo = 'Título é obrigatório!';
		if (!imagem) encontrados.imagem = 'A imagem do informativo é obrigatória!';
		if (inicioEm && fimEm && fimEm < inicioEm) encontrados.janela = 'A data final não pode ser anterior à inicial.';

		return encontrados;
	};

	const montarPayload = (imagemUrl: string): InformativoType => ({
		titulo: titulo.trim(),
		imagemUrl,
		tipo,
		ativo,
		ordem: Number(ordem) || 0,
		inicioEm: inicioDoDiaISO(inicioEm),
		fimEm: fimDoDiaISO(fimEm)
	});

	const handleSalvar = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!validar(montarErros())) return;

		setLoading(true);
		// Só sobe para o Storage quando a imagem mudou; na edição sem troca de
		// arte, a URL atual segue valendo.
		const publicarImagem = imagem.startsWith('data:')
			? uploadFileWithBase64(imagem, 'informativos')
			: Promise.resolve(imagem);

		publicarImagem
			.then((imagemUrl) => {
				const payload = montarPayload(imagemUrl);
				return informativo ? updateInformativo(informativo.id, payload) : adicionarInformativo(payload);
			})
			.then(() => {
				setLoading(false);
				toast({
					title: editando ? 'Informativo atualizado com sucesso!' : 'Informativo publicado com sucesso!',
					status: 'success',
					isClosable: true
				});
				onClose();
				afterClose();
			})
			// Sem este catch uma falha no upload deixaria o spinner girando.
			.catch((error) => {
				setLoading(false);
				const message = error?.response?.data?.message;
				toast({
					title: typeof message === 'string' ? message : 'Não foi possível salvar o informativo.',
					status: 'error',
					isClosable: true
				});
			});
	};

	const handleExcluir = () => {
		if (!informativo) return;

		confirmacao.onClose();
		setLoading(true);
		deleteInformativo(informativo.id)
			.then(() => {
				setLoading(false);
				toast({
					title: 'Informativo removido com sucesso!',
					status: 'success',
					isClosable: true
				});
				onClose();
				afterClose();
			})
			.catch((error) => {
				setLoading(false);
				const message = error?.response?.data?.message;
				toast({
					title: typeof message === 'string' ? message : 'Não foi possível remover o informativo.',
					status: 'error',
					isClosable: true
				});
			});
	};

	const handleSelectedFile = async (files: FileList | null) => {
		const arquivo = files?.[0];
		// Cancelar a janela de arquivos também dispara o change, sem arquivo: não
		// é imagem pesada, e a arte que já estava escolhida continua valendo.
		if (!arquivo) return;

		if (arquivo.size < TAMANHO_MAXIMO) {
			const base64 = await toBase64(arquivo);
			setImagem(`${base64}`);
			limparErro('imagem');
			return;
		}

		toast({
			title: 'Imagem muito pesada, selecione outra!',
			status: 'warning',
			isClosable: true
		});
	};

	const handleFechar = () => {
		resetForm();
		onClose();
		afterClose();
	};

	return (
		<>
			<DrawerForm
				isOpen={isOpen}
				onClose={handleFechar}
				titulo={editando ? 'Editar Informativo' : 'Novo Informativo'}
				formId="form-informativo"
				loading={loading}
				loadingLabel="Salvando..."
				onSubmit={handleSalvar}
				acaoDestrutiva={
					editando ? (
						<Button colorScheme="red" variant="outline" isDisabled={loading} onClick={confirmacao.onOpen}>
							Excluir
						</Button>
					) : undefined
				}
			>
				<CampoForm label="Título" htmlFor="titulo" erro={erros.titulo}>
					<Input
						id="titulo"
						placeholder="Ex.: Recesso de fim de ano"
						maxLength={120}
						onChange={(e) => {
							setTitulo(e.target.value);
							limparErro('titulo');
						}}
						value={titulo}
					/>
				</CampoForm>

				<CampoForm label="Tipo" htmlFor="tipo">
					<Select id="tipo" onChange={(e) => setTipo(e.target.value as TipoInformativo)} value={tipo}>
						{TIPOS.map((item) => (
							<option value={item.valor} key={item.valor}>
								{item.rotulo}
							</option>
						))}
					</Select>
				</CampoForm>

				{/* A prévia mostra a arte como o mural vai mostrar: inteira, com a sobra
				    preenchida pela própria arte. */}
				{imagem ? (
					<ArteInformativo
						src={imagem}
						alt="Pré-visualização do informativo"
						width="100%"
						height="150px"
						borderRadius="10px"
					/>
				) : (
					<Flex
						width="100%"
						height="150px"
						borderWidth="1px"
						borderColor="gray.200"
						borderRadius="10px"
						bgColor="paper"
						align="center"
						justify="center"
					>
						<Text fontSize="14px" color="gray.500">
							Nenhuma imagem selecionada
						</Text>
					</Flex>
				)}

				<CampoForm label="Imagem" htmlFor="imagem" erro={erros.imagem}>
					<Input
						id="imagem"
						type="file"
						accept="image/*"
						paddingTop="4px"
						onChange={(files) => handleSelectedFile(files.target.files)}
					/>
					{/* A área da arte no mural tem altura fixa (240px, 160px no celular) e
					    a largura da tela, perto de 1150 × 240 num notebook: 1920 × 400 tem a
					    mesma proporção. A arte nunca é cortada, só fica menor. */}
					<FormHelperText>
						A arte aparece inteira no mural, em qualquer formato, e o espaço que sobra é preenchido com as
						cores dela. Uma arte larga, como 1920&nbsp;×&nbsp;400&nbsp;pixels, é a que melhor ocupa o mural
						no computador; no celular ela aparece menor, e ao tocar abre em tamanho maior. Até 10&nbsp;MB.
					</FormHelperText>
				</CampoForm>

				<Flex gap="16px" flexDir={{ base: 'column', sm: 'row' }}>
					<CampoForm label="Exibir a partir de" htmlFor="inicioEm" isRequired={false} erro={erros.janela}>
						<Input
							id="inicioEm"
							type="date"
							onChange={(e) => {
								setInicioEm(e.target.value);
								limparErro('janela');
							}}
							value={inicioEm}
						/>
					</CampoForm>

					<CampoForm label="Exibir até" htmlFor="fimEm" isRequired={false}>
						<Input
							id="fimEm"
							type="date"
							onChange={(e) => {
								setFimEm(e.target.value);
								limparErro('janela');
							}}
							value={fimEm}
						/>
					</CampoForm>
				</Flex>

				<CampoForm label="Ordem no mural" htmlFor="ordem" isRequired={false}>
					<Input
						id="ordem"
						type="number"
						min={0}
						onChange={(e) => setOrdem(e.target.value)}
						value={ordem}
					/>
				</CampoForm>

				<CampoForm label="Publicado" htmlFor="ativo" isRequired={false}>
					<Switch id="ativo" size="lg" isChecked={ativo} onChange={() => setAtivo((atual) => !atual)} />
				</CampoForm>
			</DrawerForm>

			<ConfirmarExclusao
				isOpen={confirmacao.isOpen}
				onClose={confirmacao.onClose}
				onConfirmar={handleExcluir}
				titulo="Excluir informativo"
				descricao={`Tem certeza que deseja excluir "${informativo?.titulo}"? Esta ação não pode ser desfeita.`}
			/>
		</>
	);
}
